import { useAuth } from "@/utils/auth";
import { useQuery } from "@tanstack/react-query";
import { useWorklistCopy } from "@/features/i18n/worklistCopy";
import { useMemo } from "react";
import {
    clinicianWorklistQueryOptions,
    incompleteDataDiariesQueryOptions,
    worklistConsentsQueryOptions,
    worklistDismissalsQueryOptions,
} from "../api/worklistQueryOptions";
import { buildWorklistItems, countWorklistBadge } from "../utils/buildWorklistItems";
import { applyDismissedWorklistItems, useDismissedWorklistItemIds } from "../utils/completeWorklistItem";
import { buildOrphanArchivedItems, partitionOpenAndArchivedItems } from "../utils/worklistDismissals";

/** Nav badge + shared counts for the clinician worklist. */
export function useWorklistCounts(userIds: string[] = []) {
    const auth = useAuth();
    const copy = useWorklistCopy();
    // People filter asks the function for other users' diaries. That path is admin-only.
    // The function scopes everyone else to the caller. Incomplete details are filtered here.
    const canFilterPeople = auth.roles?.isAdmin === true;
    const formsUserIds = canFilterPeople ? userIds : [];
    const ownershipUserIds = canFilterPeople ? userIds : auth.id ? [auth.id] : [];

    const formsQuery = useQuery(clinicianWorklistQueryOptions(formsUserIds));
    const dataQuery = useQuery({
        ...incompleteDataDiariesQueryOptions(ownershipUserIds),
        enabled: canFilterPeople || Boolean(auth.id),
    });
    const consentsQuery = useQuery({
        ...worklistConsentsQueryOptions(ownershipUserIds),
        enabled: canFilterPeople || Boolean(auth.id),
    });
    const dismissalsQuery = useQuery({
        ...worklistDismissalsQueryOptions(),
        enabled: Boolean(auth.id),
    });

    const formsAuth = {
        roles: {
            isClinicianOnly: true,
            isPatient: false,
        },
    };

    const sessionDismissedIds = useDismissedWorklistItemIds();
    const built = useMemo(
        () =>
            applyDismissedWorklistItems(
                buildWorklistItems({
                    formDiaries: formsQuery.data?.diaries,
                    incompleteDiaries: dataQuery.data,
                    consents: consentsQuery.data,
                    auth: formsAuth,
                    unknownPatient: copy.unknownPatient,
                    consentFallback: copy.consentTask,
                    procedureDetailsLabels: {
                        injection: copy.injectionDetailsTask,
                        surgery: copy.surgeryDetailsTask,
                        shockwave: copy.shockwaveDetailsTask,
                        procedure: copy.procedureDetailsTask,
                    },
                }),
                sessionDismissedIds
            ),
        [consentsQuery.data, copy, dataQuery.data, formsQuery.data?.diaries, sessionDismissedIds]
    );

    const { openItems, archivedItems } = useMemo(() => {
        const dismissals = dismissalsQuery.data ?? [];
        const { openItems: stillOpen, archivedFromOpen } = partitionOpenAndArchivedItems(built.items, dismissals);
        const orphans = buildOrphanArchivedItems(dismissals, archivedFromOpen, {
            unknownPatient: copy.unknownPatient,
            procedureFallback: copy.procedureDetailsTask,
        });
        const archivedItems = [...archivedFromOpen, ...orphans].toSorted((a, b) => {
            const aAt = a.dismissedAt ? Date.parse(a.dismissedAt) : 0;
            const bAt = b.dismissedAt ? Date.parse(b.dismissedAt) : 0;
            return bAt - aAt;
        });
        return { openItems: stillOpen, archivedItems };
    }, [built.items, copy.procedureDetailsTask, copy.unknownPatient, dismissalsQuery.data]);

    const formTaskCount = openItems.filter((item) => item.kind === "form").length;
    const incompleteDataCount = openItems.filter((item) => item.kind === "event_details").length;
    const consentTaskCount = openItems.filter((item) => item.kind === "consent").length;
    const badgeCount = countWorklistBadge(formTaskCount, incompleteDataCount, consentTaskCount);

    return {
        canFilterPeople,
        formsQuery,
        dataQuery,
        consentsQuery,
        dismissalsQuery,
        items: openItems,
        archivedItems,
        formTaskCount,
        incompleteDataCount,
        consentTaskCount,
        badgeCount,
        truncated: formsQuery.data?.truncated ?? false,
        totalClinicianDiaries: formsQuery.data?.total ?? 0,
    };
}
